import { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import SudokuGrid from '../components/sudoku/SudokuGrid';
import { normalizeSudokuGrid } from '../components/sudoku/SudokuValidator';
import { fetchJson, sitePath, SOLVER_URL } from '../lib/api';

const emptySudokuGrid = () =>
  Array.from({ length: 9 }, () => Array.from({ length: 9 }, () => null));

const emptyMask = Array.from({ length: 9 }, () => Array.from({ length: 9 }, () => false));

export default function StuckPage() {
  const [books, setBooks] = useState([]);
  const [form, setForm] = useState({
    bookCode: '',
    puzzleNumber: '',
    puzzleType: 'sudoku',
    rawState: '',
  });
  const [manualGrid, setManualGrid] = useState(emptySudokuGrid);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchJson(sitePath('/books'))
      .then((json) => {
        const items = json.data || [];
        setBooks(items);
        if (items[0]) {
          setForm((current) => ({
            ...current,
            bookCode: current.bookCode || items[0].bookCode,
            puzzleType: items[0].puzzleType?.slug || current.puzzleType,
          }));
        }
      })
      .catch((err) => setError(err.message));
  }, []);

  const selectedBook = useMemo(
    () => books.find((book) => book.bookCode === form.bookCode) || null,
    [books, form.bookCode]
  );

  const currentPuzzleType = selectedBook?.puzzleType?.slug || form.puzzleType || 'sudoku';

  const handleSudokuCellChange = (row, col, value) => {
    setManualGrid((currentGrid) =>
      currentGrid.map((currentRow, rowIndex) =>
        rowIndex === row
          ? currentRow.map((cell, colIndex) => (colIndex === col ? value : cell))
          : currentRow
      )
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        bookCode: form.bookCode || null,
        puzzleNumber: form.puzzleNumber,
        puzzleType: currentPuzzleType,
        currentState:
          currentPuzzleType === 'sudoku'
            ? { grid: normalizeSudokuGrid(manualGrid) }
            : form.rawState,
      };

      const response = await fetchJson(`${SOLVER_URL}/api/solve/next-step`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      setResult(response);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const highlightCells = result?.highlights?.targetCells || [];
  const invalidCells = result?.highlights?.invalidCells || [];

  return (
    <>
      <Header />
      <main className="container mx-auto space-y-8 px-4 py-10">
        <section className="rounded-[2rem] border border-white/70 bg-white/85 p-8 shadow-xl shadow-slate-200/60">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-cyan">
            Book helper
          </p>
          <h1 className="mt-4 text-4xl font-semibold text-slate-900">
            Ask for the next logical step
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">
            This first release is built for book owners. Choose a book, add the puzzle number, and enter the current state so the solver can suggest the next move without spoiling the whole puzzle.
          </p>
        </section>

        <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
          <form
            onSubmit={handleSubmit}
            className="space-y-6 rounded-[2rem] border border-white/70 bg-white/90 p-8 shadow-lg shadow-slate-200/50"
          >
            <div className="grid gap-5 md:grid-cols-2">
              <label className="space-y-2 text-sm font-medium text-slate-700">
                <span>Book</span>
                <select
                  value={form.bookCode}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      bookCode: event.target.value,
                    }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3"
                >
                  {books.map((book) => (
                    <option key={book.bookCode} value={book.bookCode}>
                      {book.title}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-2 text-sm font-medium text-slate-700">
                <span>Puzzle number</span>
                <input
                  value={form.puzzleNumber}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      puzzleNumber: event.target.value,
                    }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3"
                  placeholder="Example: 42"
                />
              </label>
            </div>

            <label className="space-y-2 text-sm font-medium text-slate-700">
              <span>Puzzle type</span>
              <select
                value={currentPuzzleType}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    puzzleType: event.target.value,
                  }))
                }
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3"
              >
                <option value="sudoku">Sudoku</option>
                <option value="kakuro">Kakuro</option>
                <option value="tic-tac-toe">Tic-Tac-Toe</option>
              </select>
            </label>

            {currentPuzzleType === 'sudoku' ? (
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Current grid</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Leave blank cells empty. The helper currently supports Sudoku-style next-step reasoning first.
                  </p>
                </div>
                <SudokuGrid
                  grid={manualGrid}
                  givenMask={emptyMask}
                  onCellChange={handleSudokuCellChange}
                  errorCells={invalidCells}
                  hintCells={highlightCells}
                />
              </div>
            ) : (
              <label className="space-y-2 text-sm font-medium text-slate-700">
                <span>Current puzzle state</span>
                <textarea
                  value={form.rawState}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      rawState: event.target.value,
                    }))
                  }
                  className="min-h-[220px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 font-mono text-sm"
                  placeholder="Paste JSON or your puzzle state here."
                />
              </label>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-brand-cyan px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-magenta disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Checking…' : 'Show the next step'}
            </button>
          </form>

          <aside className="space-y-6 rounded-[2rem] border border-white/70 bg-white/90 p-8 shadow-lg shadow-slate-200/50">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-400">
                Solver response
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                Gentle help, not a full spoiler dump
              </h2>
            </div>

            {selectedBook ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                <p className="font-semibold text-slate-900">{selectedBook.title}</p>
                <p className="mt-2">{selectedBook.shortBlurb}</p>
              </div>
            ) : null}

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            {result ? (
              <div className="space-y-4">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800">
                  <p className="font-semibold uppercase tracking-[0.2em]">
                    {result.status}
                  </p>
                  <p className="mt-2">{result.explanation}</p>
                </div>
                {result.nextStep ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                    <p className="font-semibold text-slate-900">Suggested move</p>
                    <p className="mt-2">
                      Row {result.nextStep.row + 1}, column {result.nextStep.col + 1} ={' '}
                      {result.nextStep.value}
                    </p>
                    <p className="mt-2 text-slate-500">
                      Technique: {result.nextStep.technique}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                Submit a puzzle state to get the next move and a human-readable explanation.
              </div>
            )}
          </aside>
        </div>
      </main>
    </>
  );
}
